import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faMagnifyingGlass,
  faChevronDown,
  faChevronUp,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};

function AthleteCard({ athlete }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="py-0">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between text-left"
      >
        <div>
          <p className="font-semibold text-gray-800 text-base">
            {athlete.firstName} {athlete.lastName}
          </p>
          <p className="text-sm text-gray-700 mt-0.5">
            {[athlete.city, athlete.state, athlete.expectedYearOfGraduation]
              .filter(Boolean)
              .join(" • ")}
          </p>
        </div>
        <FontAwesomeIcon
          icon={expanded ? faChevronUp : faChevronDown}
          className="text-gray-400 ml-2 flex-shrink-0"
        />
      </button>
      {expanded && (
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <div className="col-span-2">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Email
            </p>
            <p className="mt-0 text-base text-gray-900">{athlete.email}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Event
            </p>
            <p className="mt-0 text-base text-gray-900">{athlete.eventName}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              High School
            </p>
            <p className="mt-0 text-base text-gray-900">{athlete.highSchool}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Position
            </p>
            <p className="mt-0 text-base text-gray-900">
              {athlete.primaryPosition}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Athlete Phone
            </p>
            <p className="mt-0 text-base text-gray-900">
              {athlete.athletePhoneNumber}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Parent Phone
            </p>
            <p className="mt-0 text-base text-gray-900">
              {athlete.parentPhoneNumber}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Date
            </p>
            <p className="mt-0 text-base text-gray-900">
              {formatDate(athlete.createdAtUTC)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CandidateAthletesMobileList({
  athletes,
  stats,
  searchQuery,
  onSearchChange,
  onExport,
  loading,
  error,
  page,
  totalPages,
  totalResults,
  startRecord,
  endRecord,
  onPageChange,
}) {
  return (
    <div className="pb-40">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-base font-semibold">Potential Athlete Signups</h1>
        <button
          onClick={onExport}
          className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50 shadow-sm"
        >
          <FontAwesomeIcon icon={faDownload} />
          Export
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-red-400"
        />
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { label: "TOTAL", value: stats.total },
          { label: "THIS WEEK", value: stats.pastWeek },
          { label: "NEW TODAY", value: stats.past24Hours },
          { label: "PAST MONTH", value: stats.pastMonth },
        ].map((s) => (
          <div key={s.label} className="p-1 text-left">
            <p className="text-xs text-gray-500 uppercase leading-tight">
              {s.label}
            </p>
            <p className="text-lg font-bold text-gray-800">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Athlete list */}
      {loading && <p className="text-center py-8 text-gray-500">Loading...</p>}
      {error && !loading && (
        <p className="text-center py-8 text-red-500">{error}</p>
      )}
      {!loading && !error && (
        <div className="divide-y divide-gray-200 ">
          {athletes.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No results found</p>
          ) : (
            athletes.map((a) => (
              <div
                key={a.id}
                className="py-2 px-3 border-gray-300 border rounded-md mb-3"
              >
                <AthleteCard athlete={a} />
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {totalResults > 0 && (
        <div className="mt-4 flex flex-col items-center gap-2 text-sm">
          <p className="text-gray-600">
            Showing {startRecord}&ndash;{endRecord} of {totalResults}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => onPageChange((p) => p - 1)}
              disabled={page === 1}
              className="px-3 py-1.5 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-gray-700">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange((p) => p + 1)}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
